import { Injectable, signal } from '@angular/core';
import { StoreService } from '../../store/store.service';
import { RouterService } from './router.service';
import { ShoppingCartService } from './shopping-cart.service';

export interface SortFilter {
  attributeName: string;
  selectionType: string;
  options: Record<string, string>;
  selectedOptions: Record<string, boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class FiltersService {
  // Store for temporary filter state (used during editing in modal/panel)
  private tempSelectedFilters = signal<Record<string, any>>({});
  private tempSortFilters = signal<SortFilter[]>([]);

  // Snapshots for cancel operation
  private initialSelectedFilters: Record<string, any> = {};
  private initialSortFilters: SortFilter[] = [];
  private initialAttributeFilters: Record<string, string[]> = {};

  constructor(
    private storeSvc: StoreService,
    private routerSvc: RouterService,
    private shoppingCartSvc: ShoppingCartService
  ) {}

  /**
   * Get the default sort filters configuration
   */
  private getDefaultSortFilters(): SortFilter[] {
    const filters = [
      {
        attributeName: this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_visualization,
        selectionType: 'single',
        options: {
          '0': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_ADA_roomFilter,
          '1': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_all_roomFilter,
        },
        selectedOptions: {
          '0': false,
          '1': true,
        },
      },
      {
        attributeName: this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_sortResults,
        selectionType: 'single',
        options: {
          '0': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_lowestPrice,
          '1': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_highestPrice,
        },
        selectedOptions: {
          '0': true,
          '1': false,
        },
      },
      {
        attributeName: this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_groupResults,
        selectionType: 'single',
        options: {
          '0': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_ratesByRoom,
          '1': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_roomByRates,
        },
        selectedOptions: {
          '0': true,
          '1': false,
        },
      },
      {
        attributeName: this.storeSvc.localeObj()?.tf_2_RoomList_FeeInclusive,
        selectionType: 'single',
        options: {
          '0': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_yes,
          '1': this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_no,
        },
        selectedOptions: {
          '0': this.storeSvc.propInfo()?.showTaxesInDisplayPrice ?? false,
          '1': !(this.storeSvc.propInfo()?.showTaxesInDisplayPrice ?? false),
        },
      },
    ];

    if(this.routerSvc.isSpecialsPage || this.routerSvc.isPromoPage) {
      return filters.slice(0, 2).concat(filters.slice(3));
    }

    if(!this.storeSvc.propInfo().displayPriceInclusiveOfTaxCheckBox) {
      return filters.slice(0, 3);
    }
    return filters;
  }

  /**
   * Initialize filters when dialog opens
   * Reads current state from store and takes a snapshot for cancel operation
   */
  public initializeFilters(filters: Record<string, any>): {
    selectedFilters: Record<string, any>;
    sortFilters: SortFilter[];
  } {
    // Get current attribute filters from store (last applied state)
    const currentAttributeFilters = this.storeSvc.appStore.selectedAttributeFilters() || {};

    // Initialize filters with selectedOptions based on store state
    const initializedFilters: Record<string, any> = {};
    Object.entries(filters).forEach(([filterKey, filterValue]: [string, any]) => {
      const selectedIds = currentAttributeFilters[filterKey] || [];

      // Build selectedOptions based on what's in the store
      const selectedOptions: Record<string, boolean> = {};
      Object.keys(filterValue.options || {}).forEach(optionId => {
        selectedOptions[optionId] = selectedIds.includes(optionId);
      });

      initializedFilters[filterKey] = {
        ...filterValue,
        selectedOptions,
      };
    });

    this.tempSelectedFilters.set(initializedFilters);

    // Get default sort filters
    const defaultSortFilters = this.getDefaultSortFilters();

    // Initialize sort filters from store (last applied state)
    const currentSortOption = this.storeSvc.appStore.selectedSortOption();
    const currentVisualizationOption = this.storeSvc.appStore.selectedVisualizationOption();
    const currentGroupBy = this.storeSvc.appStore.groupBy();

    let initializedSortFilters = defaultSortFilters;

    // Create updated sort filters with current selection
    initializedSortFilters = defaultSortFilters.map((filter) => {
      // Update Sort Results filter
      if (filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_sortResults
      && currentSortOption) {
        const newSelectedOptions: Record<string, boolean> = {};
        Object.keys(filter.selectedOptions).forEach(key => {
          newSelectedOptions[key] = key === currentSortOption;
        });

        return {
          ...filter,
          selectedOptions: newSelectedOptions,
        };
      }

      // Update Visualization filter
      if (filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_visualization
      && currentVisualizationOption) {
        const newSelectedOptions: Record<string, boolean> = {};
        Object.keys(filter.selectedOptions).forEach(key => {
          newSelectedOptions[key] = key === currentVisualizationOption;
        });

        return {
          ...filter,
          selectedOptions: newSelectedOptions,
        };
      }

      // Update Group Results filter
      if (filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_groupResults
      && currentGroupBy) {
        const newSelectedOptions: Record<string, boolean> = {};
        Object.keys(filter.selectedOptions).forEach(key => {
          // '0' = ROOMS (Rates by room), '1' = RATEPLANS (Room by rates)
          newSelectedOptions[key] = (key === '0' && currentGroupBy === 'ROOMS') ||
            (key === '1' && currentGroupBy === 'RATEPLANS');
        });

        return {
          ...filter,
          selectedOptions: newSelectedOptions,
        };
      }

      // Update Show price inclusive of fees filter - always update based on current value
      if (filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_FeeInclusive) {
        const newSelectedOptions: Record<string, boolean> = {};
        // Use ShoppingCartService value which includes user modifications
        const currentValue = this.shoppingCartSvc.showTaxesInDisplayPrice();
        Object.keys(filter.selectedOptions).forEach(key => {
          // '0' = Yes, '1' = No
          newSelectedOptions[key] = (key === '0' && currentValue) ||
            (key === '1' && !currentValue);
        });

        return {
          ...filter,
          selectedOptions: newSelectedOptions,
        };
      }

      return filter;
    });

    this.tempSortFilters.set(initializedSortFilters);

    // Take snapshot for cancel operation (deep copy)
    this.initialSelectedFilters = JSON.parse(JSON.stringify(this.tempSelectedFilters()));
    this.initialSortFilters = JSON.parse(JSON.stringify(this.tempSortFilters()));
    this.initialAttributeFilters = JSON.parse(
      JSON.stringify(this.storeSvc.appStore.selectedAttributeFilters() || {})
    );

    return {
      selectedFilters: this.tempSelectedFilters(),
      sortFilters: this.tempSortFilters(),
    };
  }

  /**
   * Update a sort filter selection
   * Creates new objects to avoid mutation
   */
  public updateSortFilter(
    currentSortFilters: SortFilter[],
    attributeName: string,
    selectionId: string,
    selected: boolean
  ): SortFilter[] {
    const updatedSortFilters = currentSortFilters.map((filter) => {
      if (filter.attributeName === attributeName) {
        // Create new filter object with updated selectedOptions
        const newSelectedOptions: Record<string, boolean> = {};
        Object.keys(filter.selectedOptions).forEach(key => {
          newSelectedOptions[key] = key === selectionId && selected;
        });

        return {
          ...filter,
          selectedOptions: newSelectedOptions,
        };
      }
      return filter;
    });

    this.tempSortFilters.set(updatedSortFilters);

    return updatedSortFilters;
  }

  /**
   * Reset filters to last applied state (for cancel operation)
   */
  public resetToSnapshot(): {
    selectedFilters: Record<string, any>;
    sortFilters: SortFilter[];
    attributeFilters: Record<string, string[]>;
  } {
    // Restore from snapshot (deep copy to avoid mutations)
    const restoredSelectedFilters = JSON.parse(JSON.stringify(this.initialSelectedFilters));
    const restoredSortFilters = JSON.parse(JSON.stringify(this.initialSortFilters));
    const restoredAttributeFilters = JSON.parse(JSON.stringify(this.initialAttributeFilters));

    this.tempSelectedFilters.set(restoredSelectedFilters);
    this.tempSortFilters.set(restoredSortFilters);

    return {
      selectedFilters: restoredSelectedFilters,
      sortFilters: restoredSortFilters,
      attributeFilters: restoredAttributeFilters,
    };
  }

  /**
   * Clear temporary state (optional cleanup)
   */
  public clearTempState(): void {
    this.tempSelectedFilters.set({});
    this.tempSortFilters.set([]);
    this.initialSelectedFilters = {};
    this.initialSortFilters = [];
    this.initialAttributeFilters = {};
  }

  /**
   * Filter rooms by visualization option (ADA rooms)
   * @param rooms - Array of rooms to filter
   * @param visualizationOption - '0' (ADA rooms only), '1' (All rooms)
   * @returns Filtered array
   */
  public filterByVisualization(rooms: any[], visualizationOption: string): any[] {
    if (!rooms || rooms.length === 0 || visualizationOption === '1') {
      return rooms; // Show all rooms
    }

    // Filter to show only ADA rooms (option '0')
    return rooms.filter(room => room.isADARoom === true);
  }

  /**
   * Sort rooms or rates by price
   * @param items - Array of rooms or rates to sort
   * @param sortOption - '0' (Lowest first), '1' (Highest first)
   * @param currency - Currency code (e.g., 'USD')
   * @param priceKey - Key to access price (e.g., 'totalPriceAfterTax', 'totalPriceBeforeTax')
   * @returns Sorted array (new array, does not mutate original)
   */
  public sortByPrice<T>(
    items: T[],
    sortOption: string,
    currency: string,
    priceKey: string
  ): T[] {
    if (!items || items.length === 0 || !priceKey) {
      return items;
    }

    const isAsc = sortOption === '0'; // '0' = Lowest price first, '1' = Highest price first

    return [...items].sort((item1: any, item2: any) => {
      const price1 = this.getPrice(item1, currency, priceKey);
      const price2 = this.getPrice(item2, currency, priceKey);

      return isAsc ? price1 - price2 : price2 - price1;
    });
  }

  /**
   * Get price from room or rate object
   * Prioritizes discounted price over regular price
   * @param item - Room or rate object
   * @param currency - Currency code
   * @param priceKey - Key to access price
   * @returns Price value or 0 if not found
   */
  private getPrice(item: any, currency: string, priceKey: string): number {
    return (
      item.discountedAveragePriceByCurrency?.[currency]?.[priceKey] ||
      item.averagePriceByCurrency?.[currency]?.[priceKey] ||
      0
    );
  }

  /**
   * Filter rooms by attribute filters (BedType, RoomCategory, Amenities, etc.)
   * @param rooms - Array of rooms to filter
   * @param attributeFilters - Object with filter keys and selected IDs
   * @returns Filtered array of rooms
   */
  public filterByAttributes(rooms: any[], attributeFilters: Record<string, string[]>): any[] {
    if (!rooms || rooms.length === 0) {
      return rooms;
    }

    // If no filters selected, return all rooms
    if (!attributeFilters || Object.keys(attributeFilters).length === 0) {
      return rooms;
    }

    // Filter rooms
    return rooms.filter(room => {
      // Check if ANY bedType in this room matches ALL selected filter categories
      const roomPasses = room.bedTypes?.some((bedType: any) => {
        // Check if this bedType matches ALL selected filter categories (AND logic across categories)
        return Object.entries(attributeFilters).every(([filterKey, selectedIds]) => {
          if (!selectedIds || selectedIds.length === 0) {
            return true; // No filter selected for this category
          }

          const bedTypeAttr = bedType.roomAttributes?.[filterKey];
          if (!bedTypeAttr) {
            return false; // BedType doesn't have this attribute
          }

          // OR logic: bedType must have ANY of the selected IDs
          return selectedIds.some(selectedId =>
            bedTypeAttr.options?.includes(Number(selectedId))
          );
        });
      });

      return roomPasses;
    });
  }

  /**
   * Process filter selection event and extract selected options
   * @param event - Filter selection event from FiltersComponent
   * @returns Object with selectedSortOption, selectedVisualizationOption, attributeFilters, groupBy,
   * and showTaxesInDisplayPrice
   */
  public processFilterSelectionEvent(event: any): {
    selectedSortOption: string | null;
    selectedVisualizationOption: string | null;
    attributeFilters: Record<string, string[]>;
    groupBy: 'ROOMS' | 'RATEPLANS' | null;
    showTaxesInDisplayPrice: boolean | null;
  } {
    let selectedSortOption: string | null = null;
    let selectedVisualizationOption: string | null = null;
    let groupBy: 'ROOMS' | 'RATEPLANS' | null = null;
    let showTaxesInDisplayPrice: boolean | null = null;

    if (event?.sortFilters) {
      // Handle Sort Results filter
      const sortFilter = event.sortFilters.find((filter: any) =>
        filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_sortResults
      );

      if (sortFilter?.selectedOptions) {
        const selectedOptionId = Object.keys(sortFilter.selectedOptions).find(
          key => sortFilter.selectedOptions[key] === true
        );
        if (selectedOptionId) {
          selectedSortOption = selectedOptionId;
        }
      }

      // Handle Visualization filter (ADA rooms)
      const visualizationFilter = event.sortFilters.find((filter: any) =>
        filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_visualization
      );

      if (visualizationFilter?.selectedOptions) {
        const selectedOptionId = Object.keys(visualizationFilter.selectedOptions).find(
          key => visualizationFilter.selectedOptions[key] === true
        );
        if (selectedOptionId) {
          selectedVisualizationOption = selectedOptionId;
        }
      }

      // Handle Group Results filter
      const groupResultsFilter = event.sortFilters.find((filter: any) =>
        filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_groupResults
      );

      if (groupResultsFilter?.selectedOptions) {
        const selectedOptionId = Object.keys(groupResultsFilter.selectedOptions).find(
          key => groupResultsFilter.selectedOptions[key] === true
        );
        if (selectedOptionId) {
          // '0' = ROOMS (Rates by room), '1' = RATEPLANS (Room by rates)
          groupBy = selectedOptionId === '0' ? 'ROOMS' : 'RATEPLANS';
        }
      }

      // Handle Show price inclusive of fees filter
      const showPriceFilter = event.sortFilters.find((filter: any) =>
        filter.attributeName === this.storeSvc.localeObj()?.tf_2_RoomList_FeeInclusive
      );

      if (showPriceFilter?.selectedOptions) {
        const selectedOptionId = Object.keys(showPriceFilter.selectedOptions).find(
          key => showPriceFilter.selectedOptions[key] === true
        );
        if (selectedOptionId !== null && selectedOptionId !== undefined) {
          // '0' = Yes, '1' = No
          showTaxesInDisplayPrice = selectedOptionId === '0';
        }
      }
    }

    // Extract attribute filters (BedType, RoomCategory, Amenities, etc.)
    const attributeFilters: Record<string, string[]> = {};
    if (event?.filters) {
      Object.entries(event.filters).forEach(([key, filter]: [string, any]) => {
        const selected = Object.entries(filter.selectedOptions || {})
          .filter(([argIgnore, isSelected]) => isSelected)
          .map(([id]) => id);
        if (selected.length > 0) {
          attributeFilters[key] = selected;
        }
      });
    }

    return { selectedSortOption, selectedVisualizationOption, attributeFilters, groupBy, showTaxesInDisplayPrice };
  }

  /**
   * Process route filter parameters and apply them to the store
   * This should be called on page load when filters data is available
   * @param filters - The filters data (Record<string, any>)
   * @returns Object with attributeFilters that were applied
   */
  public processRouteFilterParameters(filters: Record<string, any>): Record<string, string[]> {
    const queryParams = this.routerSvc.currentRouteQueryParams;

    if (!queryParams || Object.keys(queryParams).length === 0 || !filters) {
      return {};
    }

    // Find and process filter parameters (ending with '_Filter')
    const filterParams: Array<[string, string[]]> = [];

    Object.entries(queryParams).forEach(([paramKey, paramValue]) => {
      if (paramKey.endsWith('_Filter') && paramValue) {
        // Remove '_Filter' suffix and replace underscores with spaces
        const attributeName = paramKey.replace('_Filter', '').replace(/_/g, ' ');
        // Split comma-separated values
        const selectedOptionIds = String(paramValue).split(',');
        filterParams.push([attributeName, selectedOptionIds]);
      }
    });

    if (filterParams.length === 0) {
      return {};
    }

    // Build attributeFilters object based on route parameters
    const attributeFilters: Record<string, string[]> = {};

    filterParams.forEach(([paramAttributeName, selectedOptionIds]) => {
      // Find matching filter by attributeName (case-insensitive)
      const filterKey = Object.keys(filters).find((key) => {
        const filter = filters[key];
        return filter?.attributeName?.toLowerCase() === paramAttributeName.toLowerCase();
      });

      if (filterKey && filters[filterKey]) {
        const filter = filters[filterKey];
        const selectionType = filter.selectionType;
        const validOptionIds: string[] = [];

        // Validate option IDs exist in filter options
        if (selectionType === 'single') {
          // For single selection, use the first valid option ID
          if (selectedOptionIds.length > 0) {
            const optionId = selectedOptionIds[0];
            if (filter.options && filter.options[optionId] !== undefined) {
              validOptionIds.push(optionId);
            }
          }
        } else if (selectionType === 'multiple') {
          // For multiple selection, collect all valid option IDs
          selectedOptionIds.forEach((optionId) => {
            if (filter.options && filter.options[optionId] !== undefined) {
              validOptionIds.push(optionId);
            }
          });
        }

        // Only add to attributeFilters if we have valid selections
        if (validOptionIds.length > 0) {
          attributeFilters[filterKey] = validOptionIds;
        }
      }
    });

    // Apply filters to store if any were found
    if (Object.keys(attributeFilters).length > 0) {
      this.storeSvc.updateAppState('selectedAttributeFilters', attributeFilters);
    }

    return attributeFilters;
  }
}

